// src/pages/DocumentsPage.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, FileText, Search, Filter } from 'lucide-react';
import './DocumentsPage.css';
import BackToDashboard from "../components/BackToDashboard";

function DocumentsPage() {
  const [docs, setDocs] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALLA');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const BASE = (process.env.REACT_APP_API_BASE || 'https://app.kmaavfall.se/api').replace(/\/+$/, '');
  const docsURL = `${BASE}/documents`;
  const delURL = (id) => `${BASE}/delete-document/${id}`;

  async function fetchDocuments() {
    setLoading(true);
    setError('');

    try {
      const res = await fetch(docsURL, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'include',
      });

      const text = await res.text();
      let data;

      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        throw new Error(`HTTP ${res.status} – Ogiltigt JSON: ${text?.slice(0, 200)}`);
      }

      if (!res.ok) {
        throw new Error(data?.message || data?.error || `HTTP ${res.status}`);
      }

      const serverDocs = Array.isArray(data)
        ? data.filter((doc) => {
            const id = Number(doc?.id ?? doc?._id);
            return Number.isInteger(id) && id > 0;
          })
        : [];

      console.log('[DocumentsPage] serverDocs:', serverDocs);
      setDocs(serverDocs);
    } catch (err) {
      console.error('Hämtning misslyckades:', err);
      setError(String(err.message || err));
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function getStatusLabel(status) {
    const s = String(status || '').trim().toLowerCase();

    if (s === 'utkast') return 'Utkast';
    if (s === 'skickat') return 'Skickat';
    if (s === 'under behandling') return 'Under behandling';
    if (s === 'pågående') return 'Pågående';
    if (s === 'slutfört') return 'Slutfört';
    if (s === 'fakturerat') return 'Fakturerat';

    return status || 'Okänd';
  }

  function getStatusStyle(status) {
    const s = String(status || '').trim().toLowerCase();

    if (s === 'skickat' || s === 'slutfört') {
      return {
        background: '#e9f8ef',
        color: '#1f7a3d',
        border: '1px solid #cdeed8',
      };
    }

    if (s === 'utkast') {
      return {
        background: '#fff8e8',
        color: '#9a6700',
        border: '1px solid #f3dfaa',
      };
    }

    return {
      background: '#eef3ff',
      color: '#3157a3',
      border: '1px solid #d9e4ff',
    };
  }

  function formatDate(doc) {
    if (doc.pickupDate) return doc.pickupDate;
    if (doc.payload?.pickupDate) return doc.payload.pickupDate;

    if (doc.created_at) {
      try {
        return new Date(doc.created_at).toLocaleDateString('sv-SE');
      } catch {
        return '';
      }
    }

    return '';
  }

  const filteredDocs = useMemo(() => {
    const q = search.trim().toLowerCase();

    return docs.filter((doc) => {
      const payload = doc.payload || {};
      const name = String(doc.name || doc.documentName || '').toLowerCase();
      const types = String(doc.wasteTypes || payload.wasteTypes || '').toLowerCase();
      const status = String(doc.status || '').toLowerCase();
      const wasteCode = String(doc.wasteCode || payload.wasteCode || '').toLowerCase();

      const matchesSearch =
        !q ||
        name.includes(q) ||
        types.includes(q) ||
        wasteCode.includes(q);

      const matchesStatus =
        statusFilter === 'ALLA' ||
        status === statusFilter.toLowerCase();

      return matchesSearch && matchesStatus;
    });
  }, [docs, search, statusFilter]);

  async function handleDelete(id) {
    if (loading) return;
    if (!window.confirm('Vill du verkligen radera dokumentet?')) return;

    const safeId = Number(id);
    if (!Number.isInteger(safeId) || safeId <= 0) return;

    setLoading(true);
    try {
      const res = await fetch(delURL(safeId), {
        method: 'DELETE',
        headers: { Accept: 'application/json' },
        credentials: 'include',
      });

      const raw = await res.text();
      let data;

      try {
        data = raw ? JSON.parse(raw) : null;
      } catch {
        throw new Error(`HTTP ${res.status}`);
      }

      if (!res.ok || data?.error) {
        throw new Error(data?.error || `HTTP ${res.status}`);
      }

      await fetchDocuments();
    } catch (err) {
      console.error('Radering misslyckades:', err);
      alert('Kunde inte radera dokumentet.');
    } finally {
      setLoading(false);
    }
  }

  function openDocument(id) {
    const safeId = Number(id);

    if (!Number.isInteger(safeId) || safeId <= 0) {
      console.error('Ogiltigt dokument-id vid navigation:', id);
      return;
    }

    navigate(`/documents/${safeId}`);
  }

  return (
    <div className="documents-container">
      <div style={{ marginBottom: "1.2rem", display: "flex", justifyContent: "center" }}>
        <BackToDashboard label="← Tillbaka till Dashboard" />
      </div>

      <div className="documents-header-block">
        <h2 className="documents-title">Dokument</h2>
        <p className="documents-subtitle">
          Här ser du dina sparade och skickade dokument. Du kan söka, filtrera och öppna dem för att se mer.
        </p>
      </div>

      <div className="documents-toolbar">
        <div
          className="documents-search-wrap"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 10,
            flexWrap: 'wrap',
          }}
        >
          <Search size={18} className="documents-search-icon" />
          <input
            className="documents-search"
            type="text"
            placeholder="Sök på dokumentnamn, avfallstyp eller avfallskod..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={loading}
          />
        </div>

        <div
          className="documents-filter-wrap"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 12,
            flexWrap: 'wrap',
          }}
        >
          <Filter size={18} />
          <select
            className="documents-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            disabled={loading}
          >
            <option value="ALLA">Alla statusar</option>
            <option value="Utkast">Utkast</option>
            <option value="Skickat">Skickat</option>
            <option value="Under behandling">Under behandling</option>
            <option value="Pågående">Pågående</option>
            <option value="Slutfört">Slutfört</option>
            <option value="Fakturerat">Fakturerat</option>
          </select>
        </div>
      </div>

      {error && (
        <div
          style={{
            margin: '12px 0 4px',
            padding: '10px 12px',
            borderRadius: 8,
            background: '#fff3f3',
            border: '1px solid #f3d2d2',
            color: '#7a1f1f',
            fontSize: 14,
          }}
        >
          Kunde inte hämta dokument: <strong>{error}</strong>
        </div>
      )}

      {!loading && (
        <div
          className="documents-summary"
          style={{ marginBottom: 16, color: '#5f6b66', fontSize: 15 }}
        >
          Visar <strong>{filteredDocs.length}</strong> av <strong>{docs.length}</strong> dokument
        </div>
      )}

      <div className="documents-list">
        {loading ? (
          <p className="no-documents">Laddar dokument...</p>
        ) : filteredDocs.length === 0 ? (
          <p className="no-documents">Inga dokument hittades.</p>
        ) : (
          filteredDocs.map((doc) => {
            const id = doc.id ?? doc._id;
            const payload = doc.payload || {};
            const wasteCode = doc.wasteCode || payload.wasteCode || '';
            const amount = doc.amount || payload.amount || '';
            const unit = doc.unit || payload.unit || '';
            const orgNumber = doc.orgNumber || payload.orgNumber || '';
            const docName = (doc.name || doc.documentName || 'Transportärende').toString().trim();
            const status = getStatusLabel(doc.status);
            const dateText = formatDate(doc);
            const wasteTypesText = doc.wasteTypes || payload.wasteTypes || 'Ingen avfallstyp angiven';

            return (
              <div
                className="document-card"
                key={id}
                tabIndex={0}
                role="button"
                style={{ cursor: 'pointer', position: 'relative' }}
                onClick={() => openDocument(id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openDocument(id);
                  }
                }}
              >
                <button
                  className="delete-btn"
                  style={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#cc3333',
                    zIndex: 10,
                    opacity: loading ? 0.4 : 1,
                  }}
                  title="Radera dokument"
                  disabled={loading}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(id);
                  }}
                >
                  <Trash2 size={20} />
                </button>

                <div
                  className="doc-card-top"
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 12,
                    marginBottom: 10,
                    paddingRight: 36,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      gap: 10,
                      alignItems: 'flex-start',
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <div
                      style={{
                        background: '#edf7f0',
                        borderRadius: 12,
                        padding: 10,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#217a43',
                        flexShrink: 0,
                      }}
                    >
                      <FileText size={18} />
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        className="doc-card-title"
                        style={{
                          fontWeight: 700,
                          color: '#1d3b2a',
                          fontSize: '1.08rem',
                          lineHeight: 1.3,
                          marginBottom: 4,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: '100%',
                        }}
                      >
                        {docName}
                      </div>

                      <div
                        style={{
                          color: '#6b7280',
                          fontSize: '0.95rem',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {wasteTypesText}
                      </div>
                    </div>
                  </div>

                  <span
                    className="doc-card-status-badge"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '6px 10px',
                      borderRadius: 999,
                      fontSize: '0.82rem',
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      ...getStatusStyle(doc.status),
                    }}
                  >
                    {status}
                  </span>
                </div>

                <div
                  className="doc-card-meta"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: 10,
                    marginBottom: 12,
                  }}
                >
                  <div
                    className="doc-meta-box"
                    style={{ background: '#f8faf8', borderRadius: 10, padding: '10px 12px' }}
                  >
                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Datum</div>
                    <div style={{ fontWeight: 600, color: '#1f2937' }}>{dateText || '—'}</div>
                  </div>

                  <div
                    className="doc-meta-box"
                    style={{ background: '#f8faf8', borderRadius: 10, padding: '10px 12px' }}
                  >
                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Avfallskod</div>
                    <div style={{ fontWeight: 600, color: '#1f2937' }}>{wasteCode || '—'}</div>
                  </div>

                  <div
                    className="doc-meta-box"
                    style={{ background: '#f8faf8', borderRadius: 10, padding: '10px 12px' }}
                  >
                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Mängd</div>
                    <div style={{ fontWeight: 600, color: '#1f2937' }}>
                      {amount ? `${amount}${unit ? ` ${unit}` : ''}` : '—'}
                    </div>
                  </div>

                  <div
                    className="doc-meta-box"
                    style={{ background: '#f8faf8', borderRadius: 10, padding: '10px 12px' }}
                  >
                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Org.nr</div>
                    <div style={{ fontWeight: 600, color: '#1f2937' }}>{orgNumber || '—'}</div>
                  </div>
                </div>

                <div
                  className="doc-card-footer"
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: 6,
                  }}
                >
                  <span style={{ fontSize: '0.92rem', color: '#6b7280' }}>
                    Klicka för att öppna dokumentet
                  </span>

                  <span
                    className="doc-card-link"
                    style={{
                      color: '#217a43',
                      fontWeight: 700,
                      fontSize: '0.95rem',
                    }}
                  >
                    Se detaljer →
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default DocumentsPage;